import { Link } from 'react-router-dom';
import { Download, ArrowRight } from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';
import { AnalyticalField } from '@/components/AnalyticalField';

const GridBg = () => (
  <div className="absolute inset-0 grid-texture pointer-events-none" aria-hidden="true" />
);

export function Exports() {
  const exportSpecs = [
    {
      name: 'Standard Interval Export',
      format: 'CSV / XLSX',
      required: true,
      columns: ['Date', 'Interval Start', 'Queue / Skill ID', 'Offered Calls', 'Handled Calls', 'Abandoned Calls', 'AHT (avg)', 'Service Level %', 'Occupancy %', 'Agents Scheduled', 'Agents Staffed'],
      note: 'Minimum 60 days. 15- or 30-minute granularity.',
    },
    {
      name: 'Adherence Export',
      format: 'CSV / XLSX',
      required: false,
      columns: ['Agent ID', 'Date', 'Interval', 'Scheduled State', 'Actual State', 'AUX Code', 'Adherence %'],
      note: 'Optional. Required for Adherence Attribution domain analysis.',
    },
    {
      name: 'Shrinkage Log',
      format: 'CSV / XLSX',
      required: false,
      columns: ['Week / Period', 'Shrinkage Category', 'Planned %', 'Actual %', 'Headcount Impact'],
      note: 'Optional. Required for Shrinkage Drift domain analysis.',
    },
    {
      name: 'Reforecast History',
      format: 'CSV / XLSX',
      required: false,
      columns: ['Timestamp', 'Interval', 'Queue', 'Original Forecast', 'Reforecast Value', 'Actual Volume'],
      note: 'Optional. Required for Intraday Signal Collapse analysis.',
    },
  ];

  return (
    <div className="bg-zinc-950">
      <SEOHead />
      <section className="relative section-border pt-10 pb-6 md:pt-12 md:pb-8 overflow-hidden">
        <GridBg />
        <AnalyticalField variant="quiet" focalPosition="0.78,0.20" intensity={0.10} />
        <div className="container-site relative z-10">
          <div className="border-b border-zinc-800 pb-12 mb-12">
            <span className="text-xs font-mono tracking-widest uppercase text-amber-500 block mb-5">Data Specification</span>
            <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-zinc-100 leading-tight">Export Specifications</h1>
            <p className="mt-6 text-base text-zinc-400 max-w-2xl leading-relaxed">
              Reference guide for preparing data exports for submission. These specifications define exact column requirements for each export type.
            </p>
          </div>

          <div className="space-y-6">
            {exportSpecs.map((spec) => (
              <div
                key={spec.name}
                data-testid={`export-spec-${spec.name.replace(/\s+/g, '-').toLowerCase()}`}
                className="border border-zinc-800 rounded-md overflow-hidden"
              >
                <div className="bg-zinc-900 border-b border-zinc-800 px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <h3 className="font-heading text-base font-medium text-zinc-100">{spec.name}</h3>
                    <span className={`text-xs font-mono px-2 py-0.5 rounded border ${spec.required ? 'text-amber-400 bg-amber-500/10 border-amber-500/20' : 'text-zinc-500 bg-zinc-800 border-zinc-700'}`}>
                      {spec.required ? 'Required' : 'Optional'}
                    </span>
                    <span className="text-xs font-mono text-zinc-600">{spec.format}</span>
                  </div>
                </div>
                <div className="p-6 bg-zinc-950">
                  <div className="flex flex-wrap gap-2 mb-4">
                    {spec.columns.map((col) => (
                      <span key={col} className="text-xs font-mono px-2 py-1 bg-zinc-900 border border-zinc-800 rounded text-zinc-400">
                        {col}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-zinc-500">{spec.note}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 border border-zinc-800 rounded-md p-6 bg-zinc-900/20">
            <h3 className="text-sm font-medium text-zinc-300 mb-2">Questions about data preparation?</h3>
            <p className="text-sm text-zinc-500 mb-4">
              If you are uncertain about your data availability or format compatibility, complete the intake protocol.
              We will review your data readiness parameters and provide specific guidance before engagement begins.
            </p>
            <div className="flex gap-4">
              <Link to="/intake" data-testid="exports-intake-link" className="inline-flex items-center gap-2 px-5 py-2.5 bg-zinc-100 text-zinc-900 rounded-md text-sm font-medium hover:bg-white transition-colors duration-200">
                Begin Intake <ArrowRight size={14} />
              </Link>
              <Link to="/rapid-diagnostic" className="inline-flex items-center gap-2 px-5 py-2.5 border border-zinc-700 text-zinc-300 rounded-md text-sm hover:border-zinc-500 hover:text-white transition-colors duration-200">
                Full Input Requirements
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
